import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, LogOut, User, Settings, Shield, MessageCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { NotificationBell } from '../NotificationBell';

// Preload logo
const logoUrl = '/logo.png';
const logoImage = new Image();
logoImage.src = logoUrl;

const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  { label: 'Feed', href: '/feed' },
  { label: 'Projects', href: '/projects' },
  { label: 'Hackathons', href: '/hackathons' },
  { label: 'Events', href: '/events' },
  { label: 'Groups', href: '/groups' },
  { label: 'Discover', href: '/discover' },
];

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(logoImage.complete);
  const { user, profile, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!logoImage.complete) {
      logoImage.onload = () => setLogoLoaded(true);
      logoImage.onerror = () => setLogoLoaded(false);
    }
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';

  return (
    <nav className="fixed top-0 left-0 right-0 bg-slate-950/80 backdrop-blur-md z-50 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              {logoLoaded ? (
                <img 
                  src="/logo.png" 
                  alt="AUDC" 
                  className="w-10 h-10 object-contain"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-slate-800 animate-pulse" />
              )}
              <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-emerald-400 tracking-tighter -ml-1">
                .DEVS
              </span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-6">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium transition-colors tracking-tight ${
                  location.pathname === item.href
                    ? 'text-green-500'
                    : 'text-slate-400 hover:text-green-500'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Tablet Nav - condensed */}
          <div className="hidden md:flex lg:hidden items-center space-x-4">
            {NAV_ITEMS.slice(0, 5).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm font-medium transition-colors tracking-tight ${
                  location.pathname === item.href
                    ? 'text-green-500'
                    : 'text-slate-400 hover:text-green-500'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {user && (
              <>
                {/* Messages */}
                <Link
                  to="/messages"
                  className="relative p-2 text-slate-400 hover:text-green-500 transition-colors"
                >
                  <MessageCircle size={20} />
                </Link>

                {/* Notifications */}
                <NotificationBell />
              </>
            )}

            {user ? (
              /* User Menu */
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-800/50 transition-colors"
                >
                  <Avatar
                    src={profile?.avatar_url}
                    fallback={profile?.username || profile?.full_name}
                    size="sm"
                  />
                  <span className="hidden sm:block text-sm font-medium text-slate-300">
                    {profile?.username}
                  </span>
                  <ChevronDown size={16} className="text-slate-400" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-slate-900 border border-slate-800 rounded-xl shadow-xl overflow-hidden">
                    <div className="p-3 border-b border-slate-800">
                      <p className="font-medium text-white">{profile?.full_name}</p>
                      <p className="text-sm text-slate-400">@{profile?.username}</p>
                      <p className="text-xs text-green-500 mt-1">{profile?.points} points</p>
                    </div>
                    
                    <div className="p-1">
                      <Link
                        to={`/profile/${profile?.username}`}
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        <User size={16} />
                        View Profile
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        <Settings size={16} />
                        Settings
                      </Link>
                      {isAdmin && (
                        <>
                          <Link
                            to="/admin"
                            onClick={() => setIsUserMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-green-400 hover:bg-slate-800 rounded-lg transition-colors"
                          >
                            <Shield size={16} />
                            Admin Panel
                          </Link>
                          {profile?.role === 'super_admin' && (
                            <Link
                              to="/god-mode"
                              onClick={() => setIsUserMenuOpen(false)}
                              className="flex items-center gap-2 px-3 py-2 text-sm text-yellow-400 hover:bg-slate-800 rounded-lg transition-colors"
                            >
                              ⚡ God Mode
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div className="p-1 border-t border-slate-800">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Auth Buttons */
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Log In
                  </Button>
                </Link>
                <Link to="/signup" className="hidden sm:block">
                  <Button size="sm">
                    Join Us
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-slate-400 hover:text-green-500 transition-colors"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800">
          <div className="px-4 py-3 space-y-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'text-green-500 bg-slate-800'
                    : 'text-slate-300 hover:text-green-500 hover:bg-slate-800'
                }`}
              >
                {item.label}
              </Link>
            ))}
            {!user && (
              <Link
                to="/signup"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block mt-4"
              >
                <Button className="w-full">Join Us</Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
